import { ColumnDef, Row } from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  Check,
  Copy,
  Ellipsis,
  ExternalLink,
  ListChecks,
  Pencil,
  RefreshCw,
  RotateCcw,
  Tag,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useOpenDropdownRow } from './data-table';
import { DataTableColumnHeader } from './data-table-column-header';
import { ArticleEmbedStateEnum } from '@/Domain/api/KnowledgeBase/dto/EmbedState.dto';
import { ArticleEmbedStatusBadge } from '@/Domain/components/KnowledgeBase/ArticleEmbedStatusBadge';
import { ArticleRefreshFailedBadge } from '@/Domain/components/KnowledgeBase/ArticleRefreshFailedBadge';
import { Article, ArticleState } from '@/Domain/components/KnowledgeBaseArticles/types';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { isRefreshableArticleType } from '@/Domain/utils/article';
import { cn } from '@/Domain/utils/cn';
import { SCENARIO_ID } from '@/context';

type Props = {
  url: string;
  t: Translate;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

const UrlCell = ({ url, t }: Props) => {
  const [copied, setCopied] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const handleCopy = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setShowActions(false);
    }
  };

  const copyLabel = copied
    ? t('knowledge_base:articles.list.url_cell.copied')
    : t('knowledge_base:articles.list.url_cell.copy');

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="relative max-w-[320px] text-sm text-muted-foreground"
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
            onFocus={() => setShowActions(true)}
            onBlur={handleBlur}
          >
            <span className="block truncate">{url}</span>
            <div
              className={cn(
                'absolute inset-0 z-10 flex items-center backdrop-blur-[0.5px] gap-2 transition-opacity duration-300 ease-out',
                showActions ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
              )}
            >
              <button
                type="button"
                onClick={handleCopy}
                aria-label={copyLabel}
                className={cn(
                  'flex items-center gap-1 rounded-md border border-border bg-popover px-2 py-0.5 text-[10px] text-popover-foreground hover:!bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  copied ? 'pointer-events-none' : 'pointer-events-auto',
                )}
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                <span>{copyLabel}</span>
              </button>
              <button
                type="button"
                onClick={handleOpen}
                aria-label={t('knowledge_base:articles.list.url_cell.open')}
                className="pointer-events-auto flex items-center gap-1 rounded border border-border bg-popover px-2 py-0.5 text-[10px] text-popover-foreground hover:!bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <ExternalLink className="h-3 w-3" />
                <span>{t('knowledge_base:articles.list.url_cell.open')}</span>
              </button>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <span className="block break-all text-sm text-foreground">{url}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const TagsCell = ({ tags }: { tags: string[] }) => {
  const MAX_VISIBLE_TAGS = 2;
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const hiddenCount = tags.length - visibleTags.length;
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip open={isOpen} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 overflow-hidden whitespace-nowrap">
            {visibleTags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs truncate">
                {tag}
              </Badge>
            ))}
            {hiddenCount > 0 && (
              <span className="text-xs text-muted-foreground">+{hiddenCount}</span>
            )}
          </div>
        </TooltipTrigger>
        {hasBeenOpened && (
          <TooltipContent>
            <div className="flex max-w-xs flex-wrap gap-1">
              {tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

type AutoRefreshCellProps = {
  article: Article;
  available: boolean;
  isToggling: boolean;
  onToggle?: (articleId: string, enabled: boolean) => void;
  t: Translate;
};

const AutoRefreshCell = ({ article, available, isToggling, onToggle, t }: AutoRefreshCellProps) => {
  if (!isRefreshableArticleType(article.type) || !article.url) {
    return <span className="text-foreground-subtle">—</span>;
  }

  const switchEl = (
    // Stop the click bubbling to the row so toggling doesn't also open the sheet.
    // While saving, show the busy cursor and block re-clicks without dimming the
    // switch (it keeps showing its optimistic new position).
    <span
      className={cn('flex w-fit items-center', isToggling && 'cursor-wait')}
      onClick={(event) => event.stopPropagation()}
    >
      <Switch
        checked={article.auto_refresh_enabled}
        onCheckedChange={(next) => onToggle?.(String(article.id), next)}
        disabled={!available}
        className={cn(isToggling && 'pointer-events-none')}
        aria-label={t('knowledge_base:auto_refresh.toggle_aria')}
        data-testid={`article-auto-refresh-switch-${article.id}`}
      />
    </span>
  );

  if (available) return switchEl;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{switchEl}</TooltipTrigger>
        <TooltipContent>
          <p>{t('knowledge_base:auto_refresh.unavailable_tooltip')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const createColumns = (
  t: Translate,
  onEdit: (articleId: string) => void,
  onDeleteRequest: (article: Article) => void,
  onRefresh?: (articleId: string) => void,
  refreshingArticleIds?: Set<string>,
  onAutoTag?: (articleId: string) => void,
  autoTaggingArticleIds?: Set<string>,
  onToggleAutoRefresh?: (articleId: string, enabled: boolean) => void,
  autoRefreshAvailable?: boolean,
  togglingAutoRefreshIds?: Set<string>,
): ColumnDef<Article>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    meta: {
      headerClassName:
        'sticky left-0 z-10 rounded-tl-2xl w-[56px] min-w-[56px] max-w-[56px] [&:has([role=checkbox])]:pr-0 ',
      headerStyle: { left: 0 },
      cellClassName:
        'sticky left-0 z-10 w-[56px] min-w-[56px] max-w-[56px] bg-card px-4 transition-colors group-hover:!bg-accent group-data-[state=selected]:!bg-accent',
      cellStyle: { left: 0 },
      isStickyEdge: true,
      stickyPosition: 'left',
      stickyWidth: 56,
    },
  },
  {
    accessorKey: 'title',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('knowledge_base:articles.list.filters.title')}
      />
    ),
    cell: ({ row }) => {
      const title = row.getValue('title') as string;
      const articleId = String(row.original.id);
      const isRefreshing = refreshingArticleIds?.has(articleId);
      const displayTitle = title || t('knowledge_base:articles.list.untitled');
      return (
        <div className="flex items-center gap-2" data-testid={`article-row-${articleId}`}>
          <span
            className={cn(
              'truncate text-sm',
              title ? 'text-foreground' : 'italic text-muted-foreground',
            )}
            title={displayTitle}
            data-testid="article-title"
          >
            {displayTitle}
          </span>
          {isRefreshing && (
            <span
              data-testid="article-refreshing-indicator"
              className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground"
            >
              <Spinner className="h-3 w-3" />
              {t('knowledge_base:articles.list.refreshing_indicator')}
            </span>
          )}
        </div>
      );
    },
    enableHiding: false,
    meta: {
      label: 'Title',
      // Mobile: Title is non-sticky and 200px so the rest of the table can be
      // swiped into view (the sticky checkbox column already pins identity on
      // the left). Desktop: full 400px sticky pin behind the checkbox.
      headerClassName:
        'z-20 w-[200px] min-w-[200px] max-w-[200px] pr-3 md:sticky md:left-[56px] md:w-[300px] md:min-w-[300px] md:max-w-[300px] md:pr-4',
      cellClassName:
        'z-20 w-[200px] min-w-[200px] max-w-[200px] bg-card pr-3 transition-colors group-hover:!bg-accent group-data-[state=selected]:!bg-accent md:sticky md:left-[56px] md:w-[300px] md:min-w-[300px] md:max-w-[300px] md:pr-4',
      isStickyEdge: true,
      stickyPosition: 'left',
      stickyWidth: 300,
    },
  },
  {
    accessorKey: 'type',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('knowledge_base:articles.list.filters.type')}
      />
    ),
    cell: ({ row }) => {
      const type = row.getValue('type') as string;
      return (
        <Badge variant="secondary" className="capitalize whitespace-nowrap">
          {type}
        </Badge>
      );
    },
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
    meta: {
      label: 'Type',
    },
  },
  {
    accessorKey: 'created_timestamp',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('knowledge_base:articles.list.filters.created')}
      />
    ),
    cell: ({ row }) => {
      const timestamp = row.getValue('created_timestamp') as string | null;
      if (!timestamp) return <span className="text-foreground-subtle">—</span>;
      return (
        <div className="whitespace-nowrap text-sm text-muted-foreground">
          {format(new Date(timestamp), 'MMM d, yyyy')}
        </div>
      );
    },
    meta: {
      label: 'Created',
    },
  },
  {
    accessorKey: 'last_updated_timestamp',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('knowledge_base:articles.list.filters.last_updated')}
      />
    ),
    cell: ({ row }) => {
      const timestamp = row.getValue('last_updated_timestamp') as string | null;
      if (!timestamp) return <span className="text-foreground-subtle">—</span>;
      return (
        <div className="whitespace-nowrap text-sm text-muted-foreground">
          {format(new Date(timestamp), 'MMM d, yyyy')}
        </div>
      );
    },
    meta: {
      label: 'Last Updated',
    },
  },
  {
    id: 'auto_refresh',
    header: () => (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex w-fit items-center text-muted-foreground">
              <RefreshCw className="h-4 w-4" />
              <span className="sr-only">{t('knowledge_base:auto_refresh.column')}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {autoRefreshAvailable
                ? t('knowledge_base:auto_refresh.column')
                : t('knowledge_base:auto_refresh.column_unavailable')}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ),
    enableSorting: false,
    cell: ({ row }) => (
      <AutoRefreshCell
        article={row.original}
        available={!!autoRefreshAvailable}
        isToggling={!!togglingAutoRefreshIds?.has(String(row.original.id))}
        onToggle={onToggleAutoRefresh}
        t={t}
      />
    ),
    meta: {
      label: 'Auto-refresh URL',
    },
  },
  {
    accessorKey: 'embed_state',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('knowledge_base:articles.list.filters.status')}
      />
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const embedState = row.original.embed_state;
      // An embed failure outranks a refresh failure because the article is not live.
      if (
        row.original.state === ArticleState.REFRESH_FAILED &&
        embedState !== ArticleEmbedStateEnum.FAILED
      ) {
        return <ArticleRefreshFailedBadge reason={row.original.refresh_failure_reason} />;
      }
      if (!embedState) return <span className="text-foreground-subtle">—</span>;
      return <ArticleEmbedStatusBadge embedState={embedState} />;
    },
    meta: {
      label: 'Status',
    },
  },
  {
    accessorKey: 'url',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('knowledge_base:articles.list.filters.url')}
      />
    ),
    cell: ({ row }) => {
      const url = row.getValue('url') as string;
      if (!url) return <span className="text-foreground-subtle">—</span>;

      return <UrlCell url={url} t={t} />;
    },
    meta: {
      label: 'URL',
    },
  },
  {
    accessorKey: 'tags',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('knowledge_base:articles.list.filters.tags')}
      />
    ),
    cell: ({ row }) => {
      const isAutoTagging = autoTaggingArticleIds?.has(String(row.original.id));
      if (isAutoTagging) {
        return (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Spinner className="h-3.5 w-3.5" />
            <span className="text-xs">
              {t('knowledge_base:articles.list.actions.auto_tagging')}
            </span>
          </div>
        );
      }
      const tags = row.getValue('tags') as string[] | null;
      if (!tags || tags.length === 0) return <span className="text-foreground-subtle">—</span>;
      return <TagsCell tags={tags} />;
    },
    filterFn: (row, id, filterValues) => {
      if (!Array.isArray(filterValues) || filterValues.length === 0) {
        return true;
      }

      const rowTags = row.getValue<string[] | null>(id) ?? [];
      if (rowTags.length === 0) {
        return false;
      }

      return filterValues.some((filterValue) => rowTags.includes(filterValue));
    },
    meta: {
      label: 'Tags',
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    size: 60,
    cell: ({ row }) => (
      <ActionsCell
        row={row}
        t={t}
        onEdit={onEdit}
        onDeleteRequest={onDeleteRequest}
        onRefresh={onRefresh}
        refreshingArticleIds={refreshingArticleIds}
        onAutoTag={onAutoTag}
        autoTaggingArticleIds={autoTaggingArticleIds}
        onToggleAutoRefresh={onToggleAutoRefresh}
        autoRefreshAvailable={autoRefreshAvailable}
      />
    ),
    meta: {
      headerClassName: 'w-12 min-w-12 max-w-12 px-2',
      cellClassName:
        'w-12 min-w-12 max-w-12 px-2 transition-colors group-hover:bg-accent group-data-[state=selected]:bg-accent',
    },
  },
];

type ActionsCellProps = {
  row: Row<Article>;
  t: Translate;
  onEdit: (articleId: string) => void;
  onDeleteRequest: (article: Article) => void;
  onRefresh?: (articleId: string) => void;
  refreshingArticleIds?: Set<string>;
  onAutoTag?: (articleId: string) => void;
  autoTaggingArticleIds?: Set<string>;
  onToggleAutoRefresh?: (articleId: string, enabled: boolean) => void;
  autoRefreshAvailable?: boolean;
};

const ActionsCell = ({
  row,
  t,
  onEdit,
  onDeleteRequest,
  onRefresh,
  refreshingArticleIds,
  onAutoTag,
  autoTaggingArticleIds,
  onToggleAutoRefresh,
  autoRefreshAvailable,
}: ActionsCellProps) => {
  const article = row.original;
  const { openRowId, setOpenRowId } = useOpenDropdownRow();
  const isOpen = openRowId === row.id;

  return (
    <div className="flex justify-end">
      <DropdownMenu open={isOpen} onOpenChange={(next) => setOpenRowId(next ? row.id : null)}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-foreground/10">
            <span className="sr-only">{t('knowledge_base:articles.list.actions.open_menu')}</span>
            <Ellipsis className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onPointerDownOutside={() => {
            // Radix closes the dropdown on this pointerdown; the matching
            // click then bubbles to the TableRow and opens the article.
            // Swallow exactly that next click in the capture phase so the
            // outside-click only dismisses the menu — a second click is
            // required to actually open a row.
            const swallowNextClick = (ev: MouseEvent) => {
              ev.stopPropagation();
              document.removeEventListener('click', swallowNextClick, true);
            };
            document.addEventListener('click', swallowNextClick, true);
          }}
        >
          <DropdownMenuLabel>{t('knowledge_base:articles.list.actions.label')}</DropdownMenuLabel>
          <DropdownMenuItem
            aria-label={t('knowledge_base:articles.list.actions.select')}
            onClick={() => row.toggleSelected(!row.getIsSelected())}
          >
            <ListChecks className="mr-2 size-4" />
            {t('knowledge_base:articles.list.actions.select')}
          </DropdownMenuItem>
          <DropdownMenuItem
            aria-label={t('knowledge_base:articles.list.actions.edit')}
            onClick={() => onEdit(article.id)}
          >
            <Pencil className="mr-2 size-4" />
            {t('knowledge_base:articles.list.actions.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem
            aria-label={t('knowledge_base:articles.list.actions.copy_url')}
            onClick={() =>
              navigator.clipboard.writeText(
                `https://app.quickchat.ai/i/${SCENARIO_ID}/knowledge-base/articles/${article.id}`,
              )
            }
          >
            <Copy className="mr-2 size-4" />
            {t('knowledge_base:articles.list.actions.copy_url')}
          </DropdownMenuItem>
          {onAutoTag &&
            (article.tags?.length ?? 0) === 0 &&
            (() => {
              const isAutoTagging = autoTaggingArticleIds?.has(String(article.id));
              return (
                <DropdownMenuItem
                  data-testid="auto-tag-article-menu-item"
                  aria-label={t('knowledge_base:articles.list.actions.auto_tag')}
                  onClick={() => onAutoTag(String(article.id))}
                  disabled={isAutoTagging}
                >
                  {isAutoTagging ? (
                    <Spinner className="mr-2 h-4 w-4" />
                  ) : (
                    <Tag className="mr-2 h-4 w-4" />
                  )}
                  {isAutoTagging
                    ? t('knowledge_base:articles.list.actions.auto_tagging')
                    : t('knowledge_base:articles.list.actions.auto_tag')}
                </DropdownMenuItem>
              );
            })()}
          {isRefreshableArticleType(article.type) && article.url && (
            <DropdownMenuItem
              data-testid="auto-refresh-article-menu-item"
              aria-label={t('knowledge_base:articles.list.actions.auto_refresh')}
              onClick={() => onToggleAutoRefresh?.(String(article.id), true)}
              disabled={!autoRefreshAvailable}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('knowledge_base:articles.list.actions.auto_refresh')}
            </DropdownMenuItem>
          )}
          {isRefreshableArticleType(article.type) &&
            article.url &&
            (() => {
              const isArticleRefreshing = refreshingArticleIds?.has(String(article.id));
              return (
                <DropdownMenuItem
                  data-testid="refresh-article-menu-item"
                  aria-label={t('knowledge_base:articles.list.actions.refresh')}
                  onClick={() => onRefresh?.(String(article.id))}
                  disabled={isArticleRefreshing}
                >
                  {isArticleRefreshing ? (
                    <Spinner className="mr-2 size-4" />
                  ) : (
                    <RotateCcw className="mr-2 h-4 w-4" />
                  )}
                  {isArticleRefreshing
                    ? t('knowledge_base:articles.list.actions.refreshing')
                    : t('knowledge_base:articles.list.actions.refresh')}
                </DropdownMenuItem>
              );
            })()}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            aria-label={t('knowledge_base:articles.list.actions.delete')}
            onClick={() => onDeleteRequest(article)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 size-4" />
            {t('knowledge_base:articles.list.actions.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
